import React from 'react';

export const ArticleFormula: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <article className="bg-white rounded-3xl p-8 sm:p-12 space-y-8 shadow-xs border border-slate-200">
        <header className="border-b border-slate-100 pb-6">
          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-teal-50 text-[#087F9C]">
            Engineering White Paper
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] mt-3">
            How to Calculate Pipe Size: Formula, Continuity Equation &amp; Derivation
          </h1>
          <div className="text-xs text-slate-400 mt-2 flex flex-wrap gap-4">
            <span>Reviewed by PipeSizingCalculator Engineering Team</span>
            <span>Updated October 31, 2026</span>
            <span>Reading Time: 6 min</span>
          </div>
        </header>

        <section className="space-y-4 text-sm text-slate-700 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D]">1. The Law of Conservation of Mass</h2>
          <p>
            In fluid mechanics, the continuity equation derives from the conservation of mass. For steady, one-dimensional, incompressible fluid flow (such as liquid water at ambient pressures), mass flow rate remains constant across every cross-section:
          </p>
          <div className="p-4 bg-slate-50 rounded-xl font-mono text-xs border border-slate-200 text-center font-bold text-[#10283D]">
            m&#775; = &rho; &middot; Q = &rho; &middot; v &middot; A = constant
          </div>
          <p>
            Dividing by constant density &rho; yields the volumetric continuity equation: Q = v &middot; A.
          </p>

          <h2 className="font-display font-bold text-xl text-[#10283D] pt-4">2. Solving for Required Internal Bore</h2>
          <p>
            For a circular conduit of inside diameter D, the cross-sectional flow area is A = (&pi; &middot; D&sup2;) / 4. Substituting into the continuity equation:
          </p>
          <div className="p-4 bg-slate-50 rounded-xl font-mono text-xs border border-slate-200 text-center font-bold text-[#10283D]">
            Q = v &middot; (&pi; &middot; D&sup2; / 4) &rArr; D&sup2; = (4 &middot; Q) / (&pi; &middot; v) &rArr; D = &radic;((4 &middot; Q) / (&pi; &middot; v))
          </div>

          <h2 className="font-display font-bold text-xl text-[#10283D] pt-4">3. Step-by-Step Worked Example</h2>
          <p>
            Suppose a commercial branch line requires a simultaneous design flow rate of Q = 0.75 L/s (0.00075 m&sup3;/s), with a target velocity of v = 1.20 m/s:
          </p>
          <div className="p-4 bg-slate-50 rounded-xl font-mono text-xs border border-slate-200 space-y-1">
            <p>Step 1: Convert units to SI: Q = 0.00075 m&sup3;/s, v = 1.20 m/s</p>
            <p>Step 2: D_req = &radic;( (4 &times; 0.00075) / (&pi; &times; 1.20) )</p>
            <p>Step 3: D_req = &radic;( 0.0030 / 3.7699 ) = &radic;( 0.00079577 ) = 0.02821 m</p>
            <p>Step 4: D_req = 28.21 mm</p>
          </div>
          <p>
            Looking up standard BS EN 1057 copper tube: 28mm tube has an internal diameter of 26.2 mm (too small, velocity would be 1.39 m/s). 35mm tube has an internal bore of 32.6 mm, yielding an actual velocity of 0.90 m/s. Therefore, a 35mm tube is selected for low-noise commercial installations.
          </p>
        </section>

        <footer className="pt-6 border-t border-slate-100 flex flex-wrap gap-3 text-xs font-semibold">
          <a href="/water-pipe-sizing-calculator/" className="px-4 py-2 bg-[#087F9C] text-white rounded-xl hover:bg-teal-700">Calculate with Live Tool &rarr;</a>
          <a href="/pipe-size-charts/" className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200">View Pipe Dimension Charts &rarr;</a>
          <a href="/guides/darcy-weisbach-equation/" className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200">Read Darcy-Weisbach Guide &rarr;</a>
        </footer>
      </article>
    </div>
  );
};
